import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { EndpointStats } from '../../types';

interface EndpointSuccessChartProps {
  stats: EndpointStats[];
  title?: string;
}

function shortenEndpoint(endpoint: string, provider: 'fal' | 'gemini'): string {
  const prefix = provider === 'fal' ? 'fal' : 'gemini';
  const name = endpoint.replace(/^fal-ai\//, '').replace(/-preview$/, '');
  return `${prefix}: ${name}`;
}

export function EndpointSuccessChart({ stats, title = 'Endpoint Success Rate' }: EndpointSuccessChartProps) {
  const data = stats.map((s) => ({
    name: shortenEndpoint(s.endpoint, s.provider),
    completed: s.jobsCompleted,
    failed: s.jobsFailed,
    successRate: s.successRate,
    totalCalls: s.totalCalls,
  }));

  const hasData = data.length > 0;

  return (
    <div className="bg-white rounded-lg border border-surface-200 p-3 shadow-card h-full flex flex-col">
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <div className="w-7 h-7 rounded bg-violet-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-surface-800">{title}</h2>
      </div>

      {hasData ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 25 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 9, fill: '#64748b' }}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis tick={{ fontSize: 9, fill: '#64748b' }} allowDecimals={false} width={30} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
                formatter={(value: number, name: string) => {
                  const displayName = name === 'completed' ? 'Completed Jobs' : 'Failed Jobs';
                  return [`${value}`, displayName];
                }}
                labelFormatter={(label, payload) => {
                  if (payload && payload.length > 0) {
                    const item = payload[0].payload as (typeof data)[number];
                    return `${label} — ${item.successRate.toFixed(1)}% success (${item.totalCalls} API calls)`;
                  }
                  return String(label);
                }}
              />
              <Legend
                verticalAlign="top"
                height={24}
                formatter={(value: string) => (
                  <span className="text-surface-600 text-xs capitalize">{value}</span>
                )}
              />
              <Bar dataKey="completed" stackId="a" fill="#6D9E51" radius={[0, 0, 0, 0]} />
              <Bar dataKey="failed" stackId="a" fill="#A82323" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex items-center justify-center text-surface-400">
          No endpoint usage data available
        </div>
      )}
    </div>
  );
}
